// Budget + report calculations. Kept side-effect free so screens and export code can
// share the exact same numbers. Currency-combination rule (spec §19 rule 6) lives
// here in one place: raw per-currency totals are always available separately, and
// the only combined total is the explicitly-labeled APPROX_RATE_TO_EUR conversion.

import { APPROX_RATE_TO_EUR } from './format';
import type { Expense } from '../types';

export function baseSpentApprox(expenses: Expense[]): number {
  return expenses.reduce((sum, e) => sum + e.amount * (APPROX_RATE_TO_EUR[e.currency] || 0), 0);
}

export function nonBaseTotalsRaw(expenses: Expense[], baseCurrency: string): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const e of expenses) totals[e.currency] = (totals[e.currency] || 0) + e.amount;
  delete totals[baseCurrency];
  return totals;
}

export function currencyTotals(expenses: Expense[]): {
  totals: Record<string, number>;
  counts: Record<string, number>;
} {
  const totals: Record<string, number> = {};
  const counts: Record<string, number> = {};
  for (const e of expenses) {
    totals[e.currency] = (totals[e.currency] || 0) + e.amount;
    counts[e.currency] = (counts[e.currency] || 0) + 1;
  }
  return { totals, counts };
}

export function pctUsed(expenses: Expense[], totalBudget: number): number {
  if (!totalBudget) return 0;
  return Math.min(100, Math.round((baseSpentApprox(expenses) / totalBudget) * 100));
}

export function ringColor(pct: number): string {
  return pct >= 95 ? 'var(--red)' : pct >= 75 ? 'var(--amber)' : 'var(--green)';
}

/** Category totals for the Reports donut. `mode` is either 'approx' (converted to
 * base currency, always labeled ≈) or a specific currency code (raw, unconverted,
 * expenses in other currencies excluded rather than summed in). */
export function categoryTotals(
  expenses: Expense[],
  mode: string
): Array<[categoryId: string, value: number]> {
  const filtered = mode === 'approx' ? expenses : expenses.filter((e) => e.currency === mode);
  const totals: Record<string, number> = {};
  for (const e of filtered) {
    const val = mode === 'approx' ? e.amount * (APPROX_RATE_TO_EUR[e.currency] || 0) : e.amount;
    totals[e.categoryId] = (totals[e.categoryId] || 0) + val;
  }
  return Object.entries(totals).sort((a, b) => b[1] - a[1]);
}
